import { helper as $helper }        from '@/utils/helper';
import { createLabel, createPath }  from '@/fabric/objects.js';
import { ref, toRaw }               from 'vue';
import { fabric }                   from 'fabric';
import config                       from '@/etc/rosepetal.json';

import {
    canvas,
    layout,
    currentBackground,
    clearCanvasEvents,
    disableCanvasActions,
    toggleCrosshairLines,
} from '@/fabric/global.js';

import { 
    getCanvasId, 
} from '@/fabric/events/segmentation.js';

import * as rosepetalModel from "rosepetal-model";
const $sam = rosepetalModel.default.sam;
const $vlm = rosepetalModel.default.vlm;

export const SAMActive = ref(false);
export const samSteps = ref({
    instancePrepared: 0,
    instancePreparedStatus: '',
    modelReady: 0,
    modelReadyStatus: '',
});

let SAMtimeoutId = null;

function deactivateSAM() {
    SAMActive.value = false;
    console.log('SAM deactivated');
    if (SAMtimeoutId) {
        clearTimeout(SAMtimeoutId);
        SAMtimeoutId = null;
    }
}

export const resetSAMTimeout = () => {
    $sam.checkSAMhealth(config.firebaseConfig.projectId);
    if (SAMtimeoutId) clearTimeout(SAMtimeoutId);
    SAMtimeoutId = setTimeout(deactivateSAM, 30000);
    console.log('SAM timeout reset.');
}

export const activateSAM = async () => {
    if (SAMActive.value) {
        resetSAMTimeout();
        return;
    }
    
    samSteps.value = {
        instancePrepared: 0, instancePreparedStatus: '',
        modelReady: 0, modelReadyStatus: '',
    };

    let t0, t1;
    try {
        t0 = Date.now();
        const orchResult = await $vlm.checkOrchestratorHealth(config.firebaseConfig.projectId);
        t1 = Date.now();
        samSteps.value.instancePrepared = t1 - t0;
        samSteps.value.instancePreparedStatus = orchResult.status || 'unknown';

        t0 = Date.now();
        const samResult = await $sam.checkSAMhealth(config.firebaseConfig.projectId);
        await $sam.setImage(layout.value.action.edit.uri, config.firebaseConfig.projectId);
        t1 = Date.now();
        samSteps.value.modelReady = t1 - t0;
        samSteps.value.modelReadyStatus = samResult.status || 'unknown';
        await new Promise(resolve => setTimeout(resolve, 2000)); 
        
        SAMActive.value = true;
        console.log('SAM activated');
        resetSAMTimeout();

    } catch (error) {
        console.error("Failed to activate SAM:", error);
        SAMActive.value = false;
    }
}

let isDeleteMode = false;
const handleKeyDown = (e) => {
    if (e.key === 'd' || e.code === 'KeyD') {
        isDeleteMode = true;
    } else if (e.key === 'Escape' || e.code === 'Escape') {
        clearSAM();
    }
}
    
const handleKeyUp = (e) => {
    if (e.key === 'd' || e.code === 'KeyD') {
        isDeleteMode = false;
    }
}

export const teardownSAMDrawing = () => {
    document.removeEventListener('keydown', handleKeyDown);
    document.removeEventListener('keyup', handleKeyUp);
}

export const cleanupSAM = () => {
    if (SAMtimeoutId) {
        clearTimeout(SAMtimeoutId);
        SAMtimeoutId = null;
    }
    teardownSAMDrawing();
}

const createSAMPoint = (x, y, isAddition) => {
    const pointColor = isAddition ? '#22c55e' : '#ef4444';
    const point = new fabric.Circle({
        left: x - 6,
        top: y - 6,
        radius: 6,
        fill: pointColor,
        stroke: pointColor,
        strokeWidth: 1,
        selectable: false,
        evented: false,
        hoverCursor: 'default',
        moveCursor: 'default',
        hasControls: false,
        hasBorders: false,
        lockMovementX: true,
        lockMovementY: true,
        elementType: 'samPoint',
        isAddition: isAddition,
        pointX: x,
        pointY: y,
        excludeFromExport: false
    });
    
    return point;
}

const createSAMBox = (left, top, width, height) => {
    const samBox = new fabric.Rect({
        left: left,
        top: top,
        width: width,
        height: height,
        fill: 'transparent',
        stroke: '#22c55e',
        strokeWidth: 2,
        selectable: false,
        evented: false,
        hoverCursor: 'default',
        moveCursor: 'default',
        hasControls: false,
        hasBorders: false,
        lockMovementX: true,
        lockMovementY: true,
        elementType: 'samBox',
        excludeFromExport: false
    });
    
    return samBox;
}

export const setupSAMDrawing = async (projectId) => {
    clearCanvasEvents();
    disableCanvasActions();

    const currentObjects = canvas.getObjects().filter(obj => obj.elementType !== 'background' && obj.elementType !== 'crosshair' && !obj.excludeFromExport);
    if (currentObjects.length > 0 && (!canvas._samInitialState || canvas._samInitialState.objects.length !== currentObjects.length)) {
        canvas._samInitialState = canvas.toDatalessJSON([
            'uuid', 'uuid_parent', 'id', 'name', 'labeled', 'stroke', 'objid', 'color', 'elementType',
            'scaleX', 'scaleY', 'left', 'top', 'strokeWidth', '_isEraserPath', 'clipPath', 'cornerColor',
            'cornerStyle', 'globalCompositeOperation'
        ]);
    }

    canvas.getObjects().forEach(obj => {
        if (obj.elementType !== 'background' && obj.elementType !== 'crosshair' && 
            obj.elementType !== 'samPoint' && obj.elementType !== 'samBox') {
            obj.set({
                selectable: false,
                evented: false,
                hoverCursor: 'crosshair',
                moveCursor: 'crosshair'
            });
        }
    });

    canvas.defaultCursor = "crosshair";
    
    let isDeleteMode = false;
    
    const handleKeyDown = (e) => {
        if (e.key === 'd' || e.code === 'KeyD') {
            isDeleteMode = true;
        } else if (e.key === 'Escape' || e.code === 'Escape') {
            clearSAM();
        }
    };
    
    const handleKeyUp = (e) => {
        if (e.key === 'd' || e.code === 'KeyD') {
            isDeleteMode = false;
        }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    let tempBbRect, origX, origY, isDragging = false;

    canvas.on('mouse:down', async function(e) {
        if (isDeleteMode) {
            const pointer = canvas.getPointer(e.e);
            const samPoints = canvas.getObjects().filter(obj => obj.elementType === 'samPoint');
            const samBoxes = canvas.getObjects().filter(obj => obj.elementType === 'samBox');
            
            // Check for SAM points
            for (let point of samPoints) {
                const distance = Math.sqrt(
                    Math.pow(pointer.x - point.pointX, 2) + 
                    Math.pow(pointer.y - point.pointY, 2)
                );
                
                if (distance <= 12) {
                    e.e.preventDefault();
                    e.e.stopPropagation();
                    canvas._historyRecording = false;
                    canvas.remove(point);
                    canvas._historyRecording = true;
                    canvas._pushHistory();
                    
                    if (!currentBackground.value) return;
                    let image_uri = layout.value.action.edit.uri;
                    await sendToSAM(image_uri, projectId);
                    return;
                }
            }
            
            // Check for SAM boxes
            for (let box of samBoxes) {
                if (pointer.x >= box.left && pointer.x <= box.left + box.width &&
                    pointer.y >= box.top && pointer.y <= box.top + box.height) {
                    e.e.preventDefault();
                    e.e.stopPropagation();
                    canvas._historyRecording = false;
                    canvas.remove(box);
                    canvas._historyRecording = true;
                    canvas._pushHistory();
                    
                    if (!currentBackground.value) return;
                    let image_uri = layout.value.action.edit.uri;
                    await sendToSAM(image_uri, projectId);
                    return;
                }
            }
            return;
        }
        
        if (!e.target) {
            if (!currentBackground.value) return;
            const { top: bgTop, left: bgLeft, height: bgHeight, width: bgWidth } = currentBackground.value;

            canvas._historyRecording    = false;
            layout.value.selectedBB     = false;
            layout.value.canvas.isDown  = true;

            const pointer = canvas.getPointer(e.e);
            
            origX = Math.max(bgLeft, Math.min(bgLeft + bgWidth, pointer.x));
            origY = Math.max(bgTop, Math.min(bgTop + bgHeight, pointer.y));

            const labelOpt = {
                color:              '#1e4591',
                stroke:             '#1e4591',
                fill:               'rgba(255,255,255,0.2)',
                height:             1,
                width:              1,
                top:                origY,
                left:               origX,
                strokeDashArray:    [5, 5]
            };

            tempBbRect = createLabel(labelOpt);
            tempBbRect.excludeFromExport = true;
            canvas.add(tempBbRect);
        }
    });

    canvas.on('mouse:move', async (e) => {
        const pointer = canvas.getPointer(e.e);
        toggleCrosshairLines(true, pointer);

        if (!layout.value.canvas.isDown || !tempBbRect || !currentBackground.value) return;
        const { top: bgTop, left: bgLeft, height: bgHeight, width: bgWidth } = currentBackground.value;

        const clampedX = Math.max(bgLeft, Math.min(bgLeft + bgWidth, pointer.x));
        const clampedY = Math.max(bgTop, Math.min(bgTop + bgHeight, pointer.y));

        const [newLeft, newWidth]   = clampedX < origX ? [clampedX, origX - clampedX] : [origX, clampedX - origX];
        const [newTop, newHeight]   = clampedY < origY ? [clampedY, origY - clampedY] : [origY, clampedY - origY];

        tempBbRect.set({ left: newLeft, top: newTop, width: newWidth, height: newHeight });

        if (newWidth > 5 || newHeight > 5) isDragging = true;

        canvas.renderAll();
    });

    canvas.on('mouse:up', async function(e) {
        layout.value.canvas.isDown = false;
        
        if (isDeleteMode) {
            return;
        }
        
        const modalDetection = layout.value.canvas.tools.modalDetection;

        if (!currentBackground.value) return;
        const { top: bgTop, left: bgLeft, height: bgHeight, width: bgWidth } = currentBackground.value;

        let image_uri = layout.value.action.edit.uri;
        if (!isDragging) {
            const pointer = canvas.getPointer(e.e);
            
            const pointX = Math.max(bgLeft, Math.min(bgLeft + bgWidth, pointer.x));
            const pointY = Math.max(bgTop, Math.min(bgTop + bgHeight, pointer.y));

            const isAddition = !e.e.ctrlKey;
            const samPoint = createSAMPoint(pointX, pointY, isAddition);
            
            const existingSamObjects = canvas.getObjects().filter(obj => obj.elementType === 'samPoint' || obj.elementType === 'samBox');
            if (existingSamObjects.length === 0 && canvas._samInitialState) {
                canvas._historyRecording = false;
                canvas.add(samPoint);
                canvas._historyRecording = true;
                canvas._pushHistory();
            } else {
                canvas._historyRecording = false;
                canvas._mouseDownActive = false;
                canvas.add(samPoint);
                canvas._historyRecording = true;
                canvas._pushHistory();
            }

            await sendToSAM(image_uri, projectId);
        } else if (tempBbRect.width > 10 && tempBbRect.height > 10) {
            modalDetection.previousPredictedBoundingBox = [...(modalDetection.predictedBoundingBox || [])];
            modalDetection.predictedBoundingBox = [
                tempBbRect.left, 
                tempBbRect.top, 
                tempBbRect.left + tempBbRect.width, 
                tempBbRect.top + tempBbRect.height
            ];
            
            // Create persistent SAM box
            const samBox = createSAMBox(tempBbRect.left, tempBbRect.top, tempBbRect.width, tempBbRect.height);
            
            const existingSamObjects = canvas.getObjects().filter(obj => obj.elementType === 'samPoint' || obj.elementType === 'samBox');
            if (existingSamObjects.length === 0 && canvas._samInitialState) {
                canvas._historyRecording = false;
                canvas.add(samBox);
                canvas._historyRecording = true;
                canvas._pushHistory();
            } else {
                canvas._historyRecording = false;
                canvas._mouseDownActive = false;
                canvas.add(samBox);
                canvas._historyRecording = true;
                canvas._pushHistory();
            }
            
            await sendToSAM(image_uri, projectId);
        }

        if (tempBbRect) {
            canvas.remove(tempBbRect);
            canvas.renderAll();
            tempBbRect = null;
        }
        
        isDragging = false;
    });

    canvas.on('mouse:out', async function() { 
        toggleCrosshairLines(canvas, false);
    });
}

export const autoDetection = async (image_uri) => {
    layout.value.canvas.tools.modalDetection.loading = true;

    try {
        await $sam.setImage(image_uri, config.firebaseConfig.projectId);
        console.log("IMAGE SAM!");
    } catch (error) {
        console.error("Unexpected error in autoDetection:", error);
    }
    layout.value.canvas.tools.modalDetection.loading = false;
}

export const setPredictedMask = async () => {
    canvas._historyRecording = true;
    canvas._pushHistory();

    const samPoints = canvas.getObjects().filter(obj => obj.elementType === 'samPoint');
    const samBoxes = canvas.getObjects().filter(obj => obj.elementType === 'samBox');
    
    samPoints.forEach(point => canvas.remove(point));
    samBoxes.forEach(box => canvas.remove(box));

    canvas._samInitialState = null;

    layout.value.canvas.tools.modalDetection.lastPrediction         = false;
    layout.value.canvas.tools.modalDetection.predictedBoundingBox   = [];
    
    canvas.renderAll();
}

export const clearSAM = async () => {
    const samPoints = canvas.getObjects().filter(obj => obj.elementType === 'samPoint');
    const samBoxes = canvas.getObjects().filter(obj => obj.elementType === 'samBox');
    
    samPoints.forEach(point => canvas.remove(point));
    samBoxes.forEach(box => canvas.remove(box));
    
    canvas._samInitialState = null;
    
    layout.value.canvas.tools.modalDetection.loading                = false;
    layout.value.canvas.tools.modalDetection.lastPrediction         = false;
    layout.value.canvas.tools.modalDetection.predictedBoundingBox   = [];
    
    canvas.renderAll();
}

const sendToSAM = async (image_uri, projectId) => {
    try {
        const mode                  = layout.value.dataset.type === 'imageObjectDetection' ? 'detection' : 'segmentation';
        
        const samPoints = canvas.getObjects().filter(obj => obj.elementType === 'samPoint');
        const predictedPoints = samPoints.map(point => [[point.pointX, point.pointY], point.isAddition ? 1 : 0]);
        
        const predictedBoundingBox  = layout.value.canvas.tools.modalDetection.predictedBoundingBox || [];

        const response = await $sam.predict(image_uri, projectId, mode, predictedPoints, predictedBoundingBox);

        console.log("SAM prediction")
        resetSAMTimeout();

        await processPrediction(response, mode);
    } catch (error) {
        console.error("Error in samDetection:", error);
    }
}

const processPrediction = async (prediction, mode) => {
    if (layout.value.canvas.tools.modalDetection.lastPrediction) {
        const lastPrediction = toRaw(layout.value.canvas.tools.modalDetection.lastPrediction);
        canvas.remove(lastPrediction);
    }

    canvas._historyRecording = false;

    const selectedTag   = layout.value.status.currentTag;
    const tagId         = selectedTag.id || '0';
    const tagName       = selectedTag.name || 'new';
    const rawColor      = selectedTag.color || '#ff0000';
    const bbOpacity     = layout.value.canvas.filters.opacity || 0.5;

    const samOpt = {
        previousPoints: [],
        previousBB:     layout.value.canvas.tools.modalDetection.previousPredictedBoundingBox
    }

    if (mode === 'detection') {
        const { x, y, w, h } = prediction.bounding_box;
        const labelOpt = {
            id:         tagId,
            name:       tagName,
            color:      rawColor,
            fill:       $helper.hexToRgb(rawColor, bbOpacity),
            height:     h,
            width:      w,
            top:        y,
            left:       x
        }

        let bbRect = createLabel(labelOpt);

        layout.value._tempDrawingObject = bbRect;
        layout.value.canvas.tools.modalDetection.lastPrediction = bbRect;

        canvas.add(bbRect);
    }
    
    else if (mode === 'segmentation') {
        const pathOpt = {
            id:         tagId,
            name:       tagId,
            color:      rawColor,
            opacity:    layout.value.canvas.filters.opacity
        }

        let path = createPath(prediction.path, pathOpt, samOpt);
        path.uuid_parent = getCanvasId(tagId);

        layout.value._tempDrawingObject = path;
        layout.value.canvas.tools.modalDetection.lastPrediction = path;

        canvas.add(path);
    }
}